"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.computePlaceSuggestionStats = computePlaceSuggestionStats;
const place_stats_1 = require("./place-stats");
const place_stats_rooms_1 = require("./place-stats-rooms");
function addCount(map, id, name, n = 1) {
    if (!id)
        return;
    const current = map.get(id);
    if (current) {
        current.n += n;
        if (name && !current.name)
            current.name = name;
        return;
    }
    map.set(id, { name: name || id, n });
}
function sharesFromCounts(map, total) {
    if (total <= 0)
        return [];
    return [...map.entries()]
        .map(([placeId, value]) => ({
        placeId,
        name: value.name,
        n: value.n,
        share: value.n / total,
    }))
        .sort((left, right) => right.n - left.n || left.placeId.localeCompare(right.placeId));
}
function topPlaceShares(shares) {
    return shares
        .filter((share) => share.n >= place_stats_1.PLACE_STATS_MIN_PLACE_N && share.share >= place_stats_1.PLACE_STATS_MIN_PLACE_SHARE)
        .slice(0, place_stats_1.PLACE_STATS_TOP_PLACES);
}
function scopeFromRooms(rooms) {
    const ranked = [...rooms].sort((left, right) => right.share - left.share);
    const topTwo = ranked.slice(0, 2).reduce((sum, room) => sum + room.share, 0);
    if (topTwo >= 0.9)
        return 'room_specific';
    const spread = rooms.filter((room) => room.share >= 0.1).length;
    if (spread >= 4)
        return 'generic';
    return 'mixed';
}
function buildPlaceStats(counts, reportIds, computedAt, fallback) {
    const n = [...counts.values()].reduce((sum, item) => sum + item.n, 0);
    const roomsMap = new Map();
    for (const item of counts.values()) {
        const room = (0, place_stats_rooms_1.roomTypeFromPlaceName)(item.name);
        roomsMap.set(room, (roomsMap.get(room) ?? 0) + item.n);
    }
    const rooms = [...roomsMap.entries()]
        .map(([roomType, count]) => ({
        roomType: roomType,
        n: count,
        share: n > 0 ? count / n : 0,
    }))
        .sort((left, right) => right.n - left.n);
    return {
        version: place_stats_1.PLACE_STATS_VERSION,
        computedAt,
        nReports: reportIds.size,
        n,
        places: topPlaceShares(sharesFromCounts(counts, n)),
        rooms,
        scope: scopeFromRooms(rooms),
        fallback,
    };
}
function computePlaceSuggestionStats(input) {
    const computedAt = input.computedAt ?? new Date().toISOString();
    const defectCounts = new Map();
    const defectReports = new Map();
    const defectCategory = new Map();
    const categoryCounts = new Map();
    const categoryReports = new Map();
    const globalCounts = new Map();
    const transitions = new Map();
    const firstCounts = new Map();
    let nTransitions = 0;
    for (const report of input.reports) {
        const visitPlaceIds = [];
        for (const block of report.blocks) {
            if (!block.places.length)
                continue;
            if (block.defectId) {
                const counts = defectCounts.get(block.defectId) ?? new Map();
                const reports = defectReports.get(block.defectId) ?? new Set();
                for (const place of block.places) {
                    addCount(counts, place.id, place.name);
                    addCount(globalCounts, place.id, place.name);
                    if (block.categoryId) {
                        const catCounts = categoryCounts.get(block.categoryId) ?? new Map();
                        addCount(catCounts, place.id, place.name);
                        categoryCounts.set(block.categoryId, catCounts);
                        const catReports = categoryReports.get(block.categoryId) ?? new Set();
                        catReports.add(report.id);
                        categoryReports.set(block.categoryId, catReports);
                    }
                }
                defectCounts.set(block.defectId, counts);
                reports.add(report.id);
                defectReports.set(block.defectId, reports);
                if (block.categoryId && !defectCategory.has(block.defectId)) {
                    defectCategory.set(block.defectId, block.categoryId);
                }
            }
            else {
                for (const place of block.places)
                    addCount(globalCounts, place.id, place.name);
            }
            visitPlaceIds.push(block.places[0].id);
            if (visitPlaceIds.length === 1) {
                addCount(firstCounts, block.places[0].id, block.places[0].name);
            }
        }
        for (let i = 1; i < visitPlaceIds.length; i += 1) {
            const from = visitPlaceIds[i - 1];
            const to = visitPlaceIds[i];
            if (!from || !to)
                continue;
            const next = transitions.get(from) ?? new Map();
            const name = report.blocks.find((block) => block.places[0]?.id === to)?.places[0]?.name ?? to;
            addCount(next, to, name);
            transitions.set(from, next);
            nTransitions += 1;
        }
    }
    const globalPlacePrior = sharesFromCounts(globalCounts, [...globalCounts.values()].reduce((sum, item) => sum + item.n, 0));
    const defectStats = {};
    for (const [defectId, counts] of defectCounts) {
        const n = [...counts.values()].reduce((sum, item) => sum + item.n, 0);
        const fallback = n >= place_stats_1.PLACE_STATS_MIN_SUPPORT ? 'self' : defectCategory.get(defectId) ? 'category' : 'global';
        defectStats[defectId] = buildPlaceStats(counts, defectReports.get(defectId) ?? new Set(), computedAt, fallback);
    }
    const categoryStats = {};
    for (const [categoryId, counts] of categoryCounts) {
        const n = [...counts.values()].reduce((sum, item) => sum + item.n, 0);
        categoryStats[categoryId] = buildPlaceStats(counts, categoryReports.get(categoryId) ?? new Set(), computedAt, n >= place_stats_1.PLACE_STATS_MIN_SUPPORT ? 'self' : 'global');
    }
    const learnedTransitions = {};
    for (const [from, counts] of transitions) {
        const total = [...counts.values()].reduce((sum, item) => sum + item.n, 0);
        learnedTransitions[from] = sharesFromCounts(counts, total);
    }
    const source = nTransitions > 0 ? 'learned' : 'canonical';
    return {
        version: place_stats_1.PLACE_STATS_VERSION,
        computedAt,
        windowStart: input.windowStart,
        windowEnd: input.windowEnd,
        companyId: input.companyId,
        nReports: input.reports.length,
        defectStats,
        categoryStats,
        globalPlacePrior,
        orderPrior: {
            version: place_stats_1.PLACE_STATS_VERSION,
            computedAt,
            nReports: input.reports.length,
            nTransitions,
            transitions: learnedTransitions,
            firstPlaceShares: sharesFromCounts(firstCounts, [...firstCounts.values()].reduce((sum, item) => sum + item.n, 0)),
            source,
        },
    };
}
